import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import { toast } from 'react-hot-toast';
import emailService, { EmailContact } from '../services/emailService';
import ImportGoogleSheetsModal from '../components/email/ImportGoogleSheetsModal';

// Extending EmailContact with additional fields needed in this component
interface ExtendedEmailContact extends EmailContact {
  phone?: string;
  custom_fields?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
  // These fields are already in EmailContact, but defining types here for clarity
  lead_value?: number;
  lead_status?: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
  lead_source?: string;
  conversion_date?: string;
  notes?: string;
}

interface CsvImportData {
  file: File | null;
  headers: string[];
  fieldMapping: Record<string, string>;
  duplicateHandling: 'skip' | 'update' | 'create_new';
  previewData: Record<string, any>[];
}

const EmailContacts: React.FC = () => {
  const [contacts, setContacts] = useState<ExtendedEmailContact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showGoogleSheetsImportModal, setShowGoogleSheetsImportModal] = useState(false);
  const [importData, setImportData] = useState<CsvImportData>({
    file: null,
    headers: [],
    fieldMapping: {},
    duplicateHandling: 'skip',
    previewData: []
  });
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<(string | undefined)[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditLeadModal, setShowEditLeadModal] = useState(false);
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const [newContact, setNewContact] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    position: '',
    phone: '',
    lead_value: 0,
    lead_status: 'new' as 'new' | 'contacted' | 'qualified' | 'converted' | 'lost',
    lead_source: '',
    notes: ''
  });
  const [currentContact, setCurrentContact] = useState<ExtendedEmailContact | null>(null);
  const [leadInfo, setLeadInfo] = useState({
    lead_value: 0,
    lead_status: 'new' as 'new' | 'contacted' | 'qualified' | 'converted' | 'lost',
    lead_source: '',
    conversion_date: '',
    notes: ''
  });
  const { user } = useAuth();

  const standardFields = {
    email: 'Email Address',
    first_name: 'First Name',
    last_name: 'Last Name',
    company: 'Company',
    position: 'Position',
    phone: 'Phone'
  };

  useEffect(() => {
    if (user) {
      fetchContacts();
    }
  }, [user]);

  const fetchContacts = async () => {
    setIsLoading(true);
    try {
      const contacts = await emailService.getContacts();
      setContacts(contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setNewContact({
      email: '',
      first_name: '',
      last_name: '',
      company: '',
      position: '',
      phone: '',
      lead_value: 0,
      lead_status: 'new',
      lead_source: '',
      notes: ''
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/csv') {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target?.result as string;
      const lines = csv.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      
      // Parse first 5 rows for preview
      const previewData = lines.slice(1, 6).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row: Record<string, any> = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      setImportData({
        file,
        headers,
        fieldMapping: {},
        duplicateHandling: 'skip',
        previewData
      });
    };
    reader.readAsText(file);
  };

  const handleDownloadContactsTemplate = async () => {
    try {
      await emailService.downloadContactsTemplate();
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Error downloading contacts template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleFieldMapping = (csvField: string, standardField: string) => {
    setImportData(prev => ({
      ...prev,
      fieldMapping: {
        ...prev.fieldMapping,
        [csvField]: standardField
      }
    }));
  };

  const handleImport = async () => {
    if (!importData.file || !user) return;

    setIsImporting(true);
    setImportProgress(0);

    try {
      // Use emailService to import contacts - passing separate parameters instead of FormData
      const result = await emailService.importContacts(
        importData.file,
        importData.fieldMapping,
        importData.duplicateHandling as 'skip' | 'update' | 'create_new'
      );
      
      setImportProgress(100);
      toast.success(`Successfully imported ${result.imported} contacts`);
      setShowImportModal(false);
      fetchContacts();
      
      // Reset import data
      setImportData({
        file: null,
        headers: [],
        fieldMapping: {},
        duplicateHandling: 'skip',
        previewData: []
      });
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import contacts');
    } finally {
      setIsImporting(false);
      setImportProgress(0);
    }
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newContact.email.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      // Create contact using emailService
      const contactToAdd: EmailContact = {
        ...newContact,
        status: 'active'
      };
      
      const addedContact = await emailService.createContact(contactToAdd);

      // Update UI with new contact
      setContacts(prev => [addedContact as ExtendedEmailContact, ...prev]);
      setShowAddModal(false);
      resetForm();
      toast.success('Contact added successfully');
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Failed to add contact');
    }
  };

  const handleDeleteContact = async (id: string | undefined) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      await emailService.deleteContact(id);

      setContacts(prev => prev.filter(contact => contact.id !== id));
      toast.success('Contact deleted');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedContacts.length} contacts?`)) return;

    try {
      // Filter out undefined IDs and create an array of promises for each contact deletion
      const deletePromises = selectedContacts
        .filter((id): id is string => id !== undefined) // Type predicate to assert these are strings
        .map(id => emailService.deleteContact(id));
      
      await Promise.all(deletePromises);

      // Update UI after successful deletion
      setContacts(prev => prev.filter(contact => 
        contact.id && !selectedContacts.includes(contact.id)
      ));
      setSelectedContacts([]);
      toast.success(`Deleted ${selectedContacts.length} contacts`);
    } catch (error) {
      console.error('Error deleting contacts:', error);
      toast.error('Failed to delete contacts');
    }
  };

  const handleEditLead = (contact: ExtendedEmailContact) => {
    setCurrentContact(contact);
    setLeadInfo({
      lead_value: contact.lead_value || 0,
      lead_status: contact.lead_status || 'new',
      lead_source: contact.lead_source || '',
      conversion_date: contact.conversion_date || '',
      notes: contact.notes || ''
    });
    setShowEditLeadModal(true);
  };

  const handleViewContact = (contact: ExtendedEmailContact) => {
    setCurrentContact(contact);
    setLeadInfo({
      lead_value: contact.lead_value || 0,
      lead_status: contact.lead_status || 'new',
      lead_source: contact.lead_source || '',
      conversion_date: contact.conversion_date || '',
      notes: contact.notes || ''
    });
    setShowQuickViewModal(true);
  };

  const handleSaveLeadInfo = async () => {
    if (!currentContact?.id) return;

    try {
      // Update contact with lead information
      const updatedContact = await emailService.updateContact(currentContact.id, {
        lead_value: leadInfo.lead_value,
        lead_status: leadInfo.lead_status,
        lead_source: leadInfo.lead_source,
        conversion_date: leadInfo.conversion_date,
        notes: leadInfo.notes
      });

      // Update UI with updated contact
      setContacts(prev => prev.map(contact => 
        contact.id === currentContact.id ? {...contact, ...updatedContact} : contact
      ));

      setShowEditLeadModal(false);
      setCurrentContact(null);
      toast.success('Lead information updated successfully');
    } catch (error) {
      console.error('Error updating lead information:', error);
      toast.error('Failed to update lead information');
    }
  };
  
  const handleToggleSubscription = async (contact: ExtendedEmailContact) => {
    if (!contact.id) return;
    
    const newStatus = contact.status === 'unsubscribed' ? 'active' : 'unsubscribed';
    const actionText = newStatus === 'unsubscribed' ? 'unsubscribe' : 'resubscribe';
    
    if (window.confirm(`Are you sure you want to ${actionText} ${contact.email}?`)) {
      try {
        // Call API to update contact subscription status
        const updatedContact = await emailService.updateContact(contact.id, {
          status: newStatus
        });
        
        // Update UI with the new status
        setContacts(prev => prev.map(c => 
          c.id === contact.id ? {...c, status: newStatus} : c
        ));
        
        toast.success(`Contact ${actionText}d successfully`);
      } catch (error) {
        console.error(`Error ${actionText}ing contact:`, error);
        toast.error(`Failed to ${actionText} contact`);
      }
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Filter out any contacts with undefined IDs
      const validIds = filteredContacts.map(c => c.id).filter(id => id !== undefined) as (string | undefined)[];
      setSelectedContacts(validIds);
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (id: string | undefined, checked: boolean) => {
    if (!id) return; // Skip if ID is undefined
    
    if (checked) {
      setSelectedContacts(prev => [...prev, id]);
    } else {
      setSelectedContacts(prev => prev.filter(cId => cId !== id));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Email Contacts</h1>
        <div className="space-x-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded"
          >
            Add Contact
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
          >
            Import CSV
          </button>
        </div>
      </div>

      {/* Search and bulk actions */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-gray-600">
              {filteredContacts.length} of {contacts.length} contacts
            </span>
          </div>
          {selectedContacts.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {selectedContacts.length} selected
              </span>
              <button
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
              >
                Delete Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contacts table */}
      {filteredContacts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-6xl mb-4">👥</div>
          <h2 className="text-2xl font-semibold mb-2">No Contacts Found</h2>
          <p className="text-gray-600 mb-6">
            {searchTerm ? 'No contacts match your search.' : 'You haven\'t added any contacts yet.'}
          </p>
          {!searchTerm && (
            <div className="space-x-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded"
              >
                Add Contact
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
              >
                Import CSV
              </button>
              <button
                onClick={() => setShowGoogleSheetsImportModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded"
              >
                Import Google Sheets
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedContacts.length === filteredContacts.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Value
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={contact.id ? selectedContacts.includes(contact.id) : false}
                      onChange={(e) => handleSelectContact(contact.id, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleViewContact(contact)}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {contact.first_name ? contact.first_name[0] : contact.email[0].toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {contact.first_name && contact.last_name 
                            ? `${contact.first_name} ${contact.last_name}`
                            : contact.email
                          }
                        </div>
                        <div className="text-sm text-gray-500">
                          {contact.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {contact.company || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {contact.position || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {contact.lead_value ? `$${contact.lead_value.toFixed(2)}` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {contact.lead_status ? (
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        contact.lead_status === 'new' ? 'bg-blue-100 text-blue-800' :
                        contact.lead_status === 'contacted' ? 'bg-purple-100 text-purple-800' :
                        contact.lead_status === 'qualified' ? 'bg-indigo-100 text-indigo-800' :
                        contact.lead_status === 'converted' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {contact.lead_status}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      contact.status === 'active' ? 'bg-green-100 text-green-800' :
                      contact.status === 'unsubscribed' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {contact.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditLead(contact)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit Lead
                    </button>
                    <button
                      onClick={() => handleToggleSubscription(contact)}
                      className={`mr-3 ${contact.status === 'unsubscribed' ? 'text-green-600 hover:text-green-900' : 'text-yellow-600 hover:text-yellow-900'}`}
                    >
                      {contact.status === 'unsubscribed' ? 'Resubscribe' : 'Unsubscribe'}
                    </button>
                    <button
                      onClick={() => handleDeleteContact(contact.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Quick View Contact Modal */}
      {showQuickViewModal && currentContact && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Contact Details
                </h3>
                <button 
                  onClick={() => setShowQuickViewModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="flex items-center mb-4">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span className="text-2xl text-blue-600 font-medium">
                    {currentContact.first_name ? currentContact.first_name[0] : currentContact.email[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="text-lg font-medium">
                    {currentContact.first_name && currentContact.last_name 
                      ? `${currentContact.first_name} ${currentContact.last_name}` 
                      : currentContact.email}
                  </h4>
                  <p className="text-gray-600">{currentContact.email}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Company</p>
                  <p>{currentContact.company || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Position</p>
                  <p>{currentContact.position || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Phone</p>
                  <p>{currentContact.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <p>
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      currentContact.status === 'active' ? 'bg-green-100 text-green-800' :
                      currentContact.status === 'unsubscribed' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {currentContact.status}
                    </span>
                  </p>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h4 className="font-medium mb-3">Lead Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Lead Value</p>
                    <p>{currentContact.lead_value ? `$${currentContact.lead_value.toFixed(2)}` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Lead Status</p>
                    <p>
                      {currentContact.lead_status ? (
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          currentContact.lead_status === 'new' ? 'bg-blue-100 text-blue-800' :
                          currentContact.lead_status === 'contacted' ? 'bg-purple-100 text-purple-800' :
                          currentContact.lead_status === 'qualified' ? 'bg-indigo-100 text-indigo-800' :
                          currentContact.lead_status === 'converted' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {currentContact.lead_status}
                        </span>
                      ) : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Lead Source</p>
                    <p>{currentContact.lead_source || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Conversion Date</p>
                    <p>{currentContact.conversion_date || '-'}</p>
                  </div>
                </div>
                
                {currentContact.notes && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-500">Notes</p>
                    <p className="text-sm bg-gray-50 p-2 rounded">{currentContact.notes}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowQuickViewModal(false);
                  handleEditLead(currentContact);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
              >
                Edit Lead Information
              </button>
              <button
                type="button"
                onClick={() => setShowQuickViewModal(false)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Lead Modal */}
      {showEditLeadModal && currentContact && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit Lead Information: {currentContact.first_name} {currentContact.last_name}
                </h3>
                <button 
                  onClick={() => setShowEditLeadModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div>
                <label htmlFor="lead_value" className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Value ($)
                </label>
                <input
                  type="number"
                  id="lead_value"
                  min="0"
                  step="0.01"
                  value={leadInfo.lead_value}
                  onChange={(e) => setLeadInfo({...leadInfo, lead_value: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="lead_status" className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Status
                </label>
                <select
                  id="lead_status"
                  value={leadInfo.lead_status}
                  onChange={(e) => setLeadInfo({...leadInfo, lead_status: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="lead_source" className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Source
                </label>
                <input
                  type="text"
                  id="lead_source"
                  value={leadInfo.lead_source}
                  onChange={(e) => setLeadInfo({...leadInfo, lead_source: e.target.value})}
                  placeholder="Campaign name, referral, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {leadInfo.lead_status === 'converted' && (
                <div>
                  <label htmlFor="conversion_date" className="block text-sm font-medium text-gray-700 mb-1">
                    Conversion Date
                  </label>
                  <input
                    type="date"
                    id="conversion_date"
                    value={leadInfo.conversion_date}
                    onChange={(e) => setLeadInfo({...leadInfo, conversion_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
              
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  value={leadInfo.notes}
                  onChange={(e) => setLeadInfo({...leadInfo, notes: e.target.value})}
                  placeholder="Add any important notes about this lead"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                ></textarea>
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setShowEditLeadModal(false)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveLeadInfo}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
              >
                Save Lead Information
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Add New Contact</h3>
                <button 
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <form onSubmit={handleAddContact}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address*
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      value={newContact.first_name}
                      onChange={(e) => setNewContact({...newContact, first_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      value={newContact.last_name}
                      onChange={(e) => setNewContact({...newContact, last_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    value={newContact.company}
                    onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                  </label>
                  <input
                    type="text"
                    id="position"
                    value={newContact.position}
                    onChange={(e) => setNewContact({...newContact, position: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    id="phone"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Phone number"
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="lead_value" className="block text-sm font-medium text-gray-700 mb-1">
                    Lead Value ($)
                  </label>
                  <input
                    type="number"
                    id="lead_value"
                    min="0"
                    step="0.01"
                    value={newContact.lead_value}
                    onChange={(e) => setNewContact({...newContact, lead_value: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="new_lead_status" className="block text-sm font-medium text-gray-700 mb-1">
                    Lead Status
                  </label>
                  <select
                    id="new_lead_status"
                    value={newContact.lead_status}
                    onChange={(e) => setNewContact({...newContact, lead_status: e.target.value as any})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="lead_source" className="block text-sm font-medium text-gray-700 mb-1">
                    Lead Source
                  </label>
                  <input
                    type="text"
                    id="lead_source"
                    value={newContact.lead_source}
                    onChange={(e) => setNewContact({...newContact, lead_source: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Campaign name, referral, etc."
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    rows={2}
                    value={newContact.notes}
                    onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add any important notes about this lead"
                  />
                </div>
              </div>
              
              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
                >
                  Add Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Import Contacts from CSV</h3>
                <button 
                  onClick={() => setShowImportModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  &times;
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              {!importData.file ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">📁</div>
                  <h4 className="text-xl font-semibold mb-2">Upload CSV File</h4>
                  <p className="text-gray-600 mb-6">
                    Select a CSV file containing your contact data
                  </p>
                  <button
                    type="button"
                    onClick={handleDownloadContactsTemplate}
                    className="mb-4 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    Download sample template
                  </button>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold mb-4">Map CSV Fields</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {importData.headers.map((header, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <span className="text-sm font-medium w-32">{header}:</span>
                          <select
                            value={importData.fieldMapping[header] || ''}
                            onChange={(e) => handleFieldMapping(header, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          >
                            <option value="">Skip this field</option>
                            {Object.entries(standardFields).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-4">Duplicate Handling</h4>
                    <div className="space-y-2">
                      {[
                        { value: 'skip', label: 'Skip duplicates' },
                        { value: 'update', label: 'Update existing contacts' },
                        { value: 'create_new', label: 'Create new contacts anyway' }
                      ].map(option => (
                        <label key={option.value} className="flex items-center">
                          <input
                            type="radio"
                            name="duplicateHandling"
                            value={option.value}
                            checked={importData.duplicateHandling === option.value}
                            onChange={(e) => setImportData(prev => ({...prev, duplicateHandling: e.target.value as any}))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {importData.previewData.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold mb-4">Preview</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full border border-gray-300">
                          <thead>
                            <tr className="bg-gray-50">
                              {importData.headers.map((header, index) => (
                                <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {importData.previewData.map((row, index) => (
                              <tr key={index} className="border-b">
                                {importData.headers.map((header, colIndex) => (
                                  <td key={colIndex} className="px-4 py-2 text-sm text-gray-900">
                                    {row[header]}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {isImporting && (
                    <div>
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Importing contacts...</span>
                        <span>{importProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${importProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              {importData.file && (
                <button
                  onClick={handleImport}
                  disabled={isImporting || Object.keys(importData.fieldMapping).length === 0}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                >
                  {isImporting ? 'Importing...' : 'Import Contacts'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Google Sheets Import Modal */}
      <ImportGoogleSheetsModal
        isOpen={showGoogleSheetsImportModal}
        onClose={() => setShowGoogleSheetsImportModal(false)}
        onImportComplete={fetchContacts}
      />
    </div>
  );
};

export default EmailContacts;
